import { useState, useEffect, useRef } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  GalleryLightbox,
  GallerySharedStyles,
  type GalleryImage,
} from './_galleryShared';

/**
 * Auto-scrolling horizontal strip of photos pulled from the property
 * `photo_gallery`. Sits beneath PropertyOverview on the Home page. The strip
 * advances one image at a time on a timer; hovering (or opening the lightbox)
 * pauses it. Clicking any image opens the shared GalleryLightbox.
 */

const AUTO_SCROLL_MS = 3000;

export function ScrollableGallery() {
  const images = useCollectionBinding<GalleryImage>('scrollablegallery.images', {
    collection: 'photo_gallery',
    defaultLimit: 24,
  });

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const indexRef = useRef(0);
  const pausedRef = useRef(false);

  const lightboxOpen = selectedIndex !== null;

  // Auto-advance the strip one slide at a time, looping back to the start.
  useEffect(() => {
    if (images.length <= 1) return;
    const el = scrollRef.current;
    if (!el) return;

    const id = window.setInterval(() => {
      if (pausedRef.current || lightboxOpen) return;
      const slides = el.querySelectorAll<HTMLElement>('[data-slide]');
      if (slides.length === 0) return;
      indexRef.current = (indexRef.current + 1) % slides.length;
      const next = slides[indexRef.current];
      el.scrollTo({ left: next.offsetLeft, behavior: 'smooth' });
    }, AUTO_SCROLL_MS);

    return () => window.clearInterval(id);
  }, [images.length, lightboxOpen]);

  if (images.length === 0) return null;

  return (
    <section className="bg-[var(--vibe-color-secondary)] pb-8 md:pb-12">
      <div
        ref={scrollRef}
        className="scroll-gallery"
        onMouseEnter={() => {
          pausedRef.current = true;
        }}
        onMouseLeave={() => {
          pausedRef.current = false;
        }}
      >
        {images.map((img, i) => {
          if (!img.image) return null;
          return (
            <button
              type="button"
              key={img.image || i}
              data-slide
              className="scroll-gallery__item gallery-item"
              onClick={() => setSelectedIndex(i)}
              aria-label={`View ${img.alt_text || img.title || 'property photo'} (${i + 1})`}
            >
              <VibesitesImage
                url={img.image}
                alt={img.alt_text || img.title || ''}
                className="w-full h-full object-cover"
              />
            </button>
          );
        })}
      </div>

      <GalleryLightbox
        images={images}
        selectedIndex={selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <GallerySharedStyles />

      <style>{`
        .scroll-gallery {
          display: flex;
          gap: 0;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        .scroll-gallery::-webkit-scrollbar {
          display: none;
        }
        .scroll-gallery__item {
          flex: 0 0 auto;
          width: clamp(240px, 20vw, 400px);
          aspect-ratio: 1 / 1;
          padding: 0;
          margin: 0;
          border: none;
          background: none;
          cursor: pointer;
          overflow: hidden;
          scroll-snap-align: start;
        }
        .scroll-gallery__item img {
          transition: transform 0.6s ease;
        }
        .scroll-gallery__item:hover img {
          transform: scale(1.05);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScrollableGallery',
  displayName: 'Scrollable Gallery',
  tokens: {},
  collectionBindings: {
    'scrollablegallery.images': {
      label: 'Gallery Images',
      collection: 'photo_gallery',
      defaultLimit: 24,
      description: 'Photos that auto-scroll in the strip; click to open the lightbox.',
    },
  },
};

(ScrollableGallery as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default ScrollableGallery;
